import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Link, getRouteApi, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, LogOut, Plus } from "lucide-react";
import { logout } from "@/lib/api";
import { TernMark } from "./TernMark";

const route = getRouteApi("/$ws");

export function WorkspaceSwitcher() {
  const { me, workspace } = route.useRouteContext();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: async () => {
      qc.clear();
      await navigate({ to: "/login" });
    },
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button aria-label={`${t("nav.switchWorkspace")}: ${workspace.name}`} className="btn btn-ghost h-9 min-w-0 flex-1 justify-start gap-2 px-1 font-medium data-[state=open]:bg-hover">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border-strong bg-elevated text-fg">
            <TernMark className="h-4 w-4" />
          </span>
          <span className="min-w-0 truncate text-left text-[13px]" title={workspace.name}>{workspace.name}</span>
          <ChevronDown className="h-3 w-3 shrink-0 text-faint" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content align="start" sideOffset={6} collisionPadding={12} className="z-50 max-h-[var(--radix-dropdown-menu-content-available-height)] w-[280px] max-w-[calc(100vw-24px)] overflow-y-auto rounded-xl border border-border bg-elevated p-1.5 shadow-xl">
          <div className="truncate px-2.5 py-2 text-[12px] text-muted" title={me.user.email}>{me.user.email}</div>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Label className="px-2.5 pb-1.5 pt-2 text-[11px] font-medium text-muted">{t("nav.switchWorkspace")}</DropdownMenu.Label>
          {me.workspaces.map((w) => (
            <DropdownMenu.Item key={w.id} asChild>
              <Link to="/$ws/inbox" params={{ ws: w.slug }} aria-current={w.id === workspace.id ? "true" : undefined} className={`flex min-h-9 cursor-default items-center gap-2 rounded-md px-2.5 text-[13px] outline-none data-[highlighted]:bg-hover ${w.id === workspace.id ? "bg-active font-medium" : ""}`}>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-hover text-[10px] font-semibold text-muted">{w.name.slice(0, 1).toUpperCase()}</span>
                <span className="flex-1 truncate">{w.name}</span>
                <span className="shrink-0 text-[11px] font-normal text-muted">{t(`workspace.role.${w.role}`)}</span>
                <span className="w-3.5 shrink-0">{w.id === workspace.id && <Check className="h-3.5 w-3.5 text-fg" />}</span>
              </Link>
            </DropdownMenu.Item>
          ))}
          <DropdownMenu.Item asChild>
            <a href="/new" className="flex h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted outline-none data-[highlighted]:bg-hover data-[highlighted]:text-fg">
              <Plus className="h-3.5 w-3.5" /> {t("onboarding.allTeams")}
            </a>
          </DropdownMenu.Item>
          <DropdownMenu.Separator className="my-1 h-px bg-border" />
          <DropdownMenu.Item
            disabled={signOut.isPending}
            className="flex h-9 cursor-default items-center gap-2.5 rounded-md px-2.5 text-[13px] text-muted outline-none data-[highlighted]:bg-hover data-[highlighted]:text-fg data-[disabled]:opacity-50"
            onSelect={() => signOut.mutate()}
          >
            <LogOut className="h-3.5 w-3.5" /> {t("nav.signOut")}
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
